/**
 * formatUploadError — diagnostic-rich error string for upload failures.
 *
 * Replaces the generic "Upload failed" fallback so the artist (and we) can
 * tell at a glance whether the failure was caused by:
 *   • Reverse-proxy size cap (413) — most common on production
 *   • Proxy / backend timeout (502 / 504)
 *   • Backend exception (500)
 *   • Network failure before the request reached the server
 *   • Backend validation (400, with a `detail` string)
 *
 * Designed to be safe in JSX — always returns a string, never an object.
 */
export function formatUploadError(err, file, startedAt) {
  const fileLabel = file
    ? `${file.name} (${(file.size / (1024 * 1024)).toFixed(1)} MB)`
    : 'file';
  const elapsed = startedAt ? `${Math.round((Date.now() - startedAt) / 1000)}s` : '';

  // Axios error with a response from the server
  if (err?.response) {
    const status = err.response.status;
    const detail = err.response.data?.detail;
    const detailStr = typeof detail === 'string'
      ? detail
      : (detail?.error || detail?.message || JSON.stringify(detail || err.response.data || {}).slice(0, 240));

    if (status === 413) {
      return `Upload rejected by the deployment proxy (413). ${fileLabel} is too large for the production reverse-proxy. Ask the studio admin to raise the proxy limit, or use a smaller file. Server detail: ${detailStr}`;
    }
    if (status === 504 || status === 502) {
      return `Upload timed out (${status}) after ${elapsed} for ${fileLabel}. The deployment proxy killed the connection before the backend finished saving. ${detailStr}`;
    }
    if (status === 401 || status === 403) {
      return `Session expired (${status}). Refresh the page and log in again.`;
    }
    return `Upload failed (${status}) for ${fileLabel} after ${elapsed}. ${detailStr}`;
  }

  // Axios error with no response (network error, CORS, aborted, etc.)
  if (err?.request) {
    const code = err.code || 'NETWORK';
    const msg = err.message || 'no response from server';
    return `Network error (${code}) uploading ${fileLabel} after ${elapsed}. ${msg}. This usually means the proxy dropped the connection — file may be too large for the deployment.`;
  }

  // Anything else
  return `Upload failed for ${fileLabel}: ${err?.message || String(err) || 'unknown error'}`;
}
